import { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../services/authentication/authContext';
import { supabase } from '../../../services/authentication/supabaseService';
import { setInitialPassword } from '../../../services/user/userService';
import { Header } from '../landingpage/sections/Header';

export default function InitialPassword() {
  const { t } = useTranslation();
  const { user, refreshProfile } = useAuth();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState<'saved' | 'uncertain' | null>(null);
  const inFlight = useRef(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (inFlight.current) return;
    if (password !== repeat) { setError(t('managedSignup.passwordMismatch')); return; }
    inFlight.current = true; setSaving(true); setError('');
    let updated = false;
    try {
      await setInitialPassword(password);
      updated = true;
      // Admin password updates revoke existing Auth sessions. Establish a fresh
      // customer session before attempting any subsequent API request.
      const { error: signInError } = await supabase.auth.signInWithPassword({ email: user?.email ?? '', password });
      setPassword(''); setRepeat('');
      if (signInError) { setPasswordSaved('saved'); return; }
      await refreshProfile();
    } catch (err) {
      if (updated) { setPassword(''); setRepeat(''); setPasswordSaved('saved'); return; }
      console.error('Failed to set initial password');
      const code = err instanceof Error ? err.message : '';
      if (code === 'initial_password_save_failed') {
        // The Auth write can succeed even when clearing our DB flag fails.
        // Renew that revoked session so a different-password retry remains usable.
        try {
          const { error: renewalError } = await supabase.auth.signInWithPassword({ email: user?.email ?? '', password });
          if (renewalError) { setPassword(''); setRepeat(''); setPasswordSaved('uncertain'); return; }
        } catch { setPassword(''); setRepeat(''); setPasswordSaved('uncertain'); return; }
      }
      const known = ['initial_password_not_allowed', 'email_confirmation_required', 'invalid_password', 'password_must_change', 'password_check_failed', 'initial_password_save_failed'];
      setError(t(`managedSignup.${known.includes(code) ? code : 'passwordFailed'}`));
    } finally { inFlight.current = false; setSaving(false); }
  };
  return (
    <div className="relative overflow-hidden min-h-screen bg-landingPageBG text-white dark:bg-landingPageBG dark:text-white">
      <Header />
      <main className="flex justify-center px-5 pt-12 pb-12 sm:pt-20">
        <section className="relative z-10 w-full max-w-md h-fit rounded-2xl border border-white/10 p-6 sm:p-8 bg-white/5 backdrop-blur-sm dark:border-white/10 dark:bg-white/5">
          <h1 className="text-2xl font-semibold">{t('managedSignup.choosePassword')}</h1>
          <p className="mt-2 mb-6 text-sm text-white/70 dark:text-white/70">{t('managedSignup.passwordDescription')}</p>
          {passwordSaved ? (
            <div>
              <p role="status">{t(passwordSaved === 'saved' ? 'managedSignup.passwordSavedLogin' : 'managedSignup.passwordPossiblySavedLogin')}</p>
              <button className="min-h-[44px] underline" onClick={async () => { await supabase.auth.signOut(); window.location.assign('/login'); }}>{t('managedSignup.backToLogin')}</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <label className="block text-sm">
                {t('managedSignup.password')}
                <input required minLength={8} autoComplete="new-password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="mt-2 block w-full min-h-[44px] px-4 py-3 border border-white/20 rounded-xl bg-white/10 text-white focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent dark:border-white/20 dark:bg-white/10 dark:text-white dark:focus:ring-accent dark:focus:border-transparent" />
              </label>
              <label className="block text-sm">
                {t('managedSignup.repeatPassword')}
                <input required minLength={8} autoComplete="new-password" type="password" value={repeat} onChange={e => setRepeat(e.target.value)} className="mt-2 block w-full min-h-[44px] px-4 py-3 border border-white/20 rounded-xl bg-white/10 text-white focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent dark:border-white/20 dark:bg-white/10 dark:text-white dark:focus:ring-accent dark:focus:border-transparent" />
              </label>
              {error && <p role="alert" className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300">{error}</p>}
              <button disabled={saving} className="min-h-[44px] w-full px-6 py-3 text-base font-semibold bg-accent text-white hover:bg-accent/90 rounded-2xl transition disabled:opacity-50 disabled:cursor-not-allowed dark:bg-accent dark:text-white dark:hover:bg-accent/90">{t(saving ? 'common.loading' : 'managedSignup.savePassword')}</button>
            </form>
          )}
          <button className="mt-4 min-h-[44px] text-sm text-white/70 hover:text-white transition underline dark:text-white/70 dark:hover:text-white" onClick={() => void supabase.auth.signOut()}>{t('accountSetup.logout')}</button>
        </section>
      </main>
    </div>
  );
}
